import { useCallback, useContext, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import MarkdownRender from 'LuxKit/MarkdownRender'
import { isSenderLE, isSenderSystem, isSenderUser } from 'supportAssistant/utils/messageUtils'
import FeedbackModal from 'supportAssistant/components/Chat/FeedbackModal'
import Loading from 'components/Common/Loading/LoadingAnimation'
import LineThumbsDownIcon from 'LuxKit/Icons/line/LineThumbsDownIcon'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import SolidThumbsDownIcon from 'LuxKit/Icons/solid/SolidThumbsDownIcon'
import SolidThumbsUpIcon from 'LuxKit/Icons/solid/SolidThumbsUpIcon'
import ModalContext from 'contexts/ModalContext'
import AgentAvatar from 'supportAssistant/components/Support/AgentAvatar'
import MessageMetaComponents from './MessageMeta/MessageMetaComponents'
import { phoneLinksDecorator } from 'lib/string/markdownUtils'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectCurrentChat } from 'supportAssistant/selectors/selectCurrentChat'
import { sendMessageFeedback } from 'api/supportAssistant'
import { getCurrentUserId } from 'selectors/accountSelectors'
import { postMessageFeedback } from 'actions/supportAssistant/supportAssistantActions'

const Container = styled.div`
  display: grid;
  grid-gap: ${rem(2)};
  grid-template-columns: min-content 1fr;
  grid-template-areas:
    "avatar message"
    ". footer"
    ". feedback";
  width: fit-content;
  max-width: 95%;

  ${mediaQueryUp.tablet} {
    max-width: calc(90% + ${rem(60)});
  }

  &.is-le-sender {
    grid-template-areas:
      "avatar message"
      ". feedback"
      ". footer";

    ${mediaQueryUp.tablet} {
      grid-template-columns: min-content 1fr ${rem(60)};
      grid-template-areas:
        "avatar message feedback"
        ". meta ."
        ". footer .";
    }
  }

  &.is-system-sender {
    grid-template-columns: auto;
    grid-template-areas: "message";
    margin: auto;
  }

  &.is-le-sender {
    // Next line is temp fix, overhaul of the use of grids need to be undertaken
    margin-top: ${rem(16)};
    margin-inline-end: auto;
    margin-inline-start: ${rem(8)};
    border-radius: ${rem(4)};
  }

  &.is-user-sender {
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      "message avatar"
      "footer ."
      "meta meta";
    justify-self: end;
    margin-inline-start: auto;
    // FIX: Consider moving message meta out of Message.tsx
    max-width: 100%;
    width: 100%;

    & > * {
      margin-inline-start: auto;
    }
  }

  // Chat layout keeps physical positions (bot=left, user=right) in both
  // directions. This counteracts CSS Grid's automatic RTL column reversal.
  [dir="rtl"] & {
    &.is-le-sender {
      grid-template-columns: 1fr min-content;
      grid-template-areas:
        "message avatar"
        "feedback ."
        "footer .";

      ${mediaQueryUp.tablet} {
        grid-template-columns: ${rem(60)} 1fr min-content;
        grid-template-areas:
          "feedback message avatar"
          ". meta ."
          ". footer .";
      }
    }

    &.is-user-sender {
      grid-template-columns: min-content 1fr;
      grid-template-areas:
        "avatar message"
        ". footer"
        "meta meta";
      justify-self: start;
    }
  }

  // hover on a message should control visibility of thumb-icons
  &:hover {
    .feedback {
      opacity: 1;
      visibility: visible;
      pointer-events: auto;
      transition: opacity 0.2s 0.3s;
    }

    .thumb-up:not(.fade-out),
    .thumb-down:not(.fade-out) {
      cursor: pointer;
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
      transition: opacity 0.2s 0.3s;
    }
  }
`

const Avatar = styled.div`
  grid-area: avatar;
  width: ${rem(24)};
  height: ${rem(24)};
  align-self: end;
`

const MessageMetaContent = styled.div`
  // make meta on user message appear on the left styled as if it is LE message
  grid-area: meta;
  width: 90%; // fix existing mobile display bug

  &.is-le-sender {
    margin-top: ${rem(16)};
  }
  
  &.is-user-sender {
    margin-inline-end: auto;
    margin-inline-start: ${rem(34)};
    border-radius: ${rem(4)};
  }

  ${mediaQueryUp.tablet} {
    width: ${rem(500)};
  }
`

const MessageContent = styled.div`
  grid-area: message;
  padding: ${rem(8)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};

  &:not(.is-user-sender) {
    margin-inline-end: auto;
    margin-inline-start: ${rem(8)};
  }

  &.is-user-sender {
    background-color: ${props => props.theme.palette.neutral.surface.darkBackground};
    color: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const SystemMessageContent = styled.div`
  grid-area: message;
  text-align: center;
`

const MessageFeedback = styled.div`
  grid-area: feedback;
  min-height: ${rem(24)};
  display: grid;
  position: relative;
  grid-template-columns: auto auto;
  justify-content: start;
  margin-inline-start: ${rem(4)};
  margin-bottom: ${rem(4)};

  // hide thumb-icons only on desktop for hover functionality
  ${mediaQueryUp.tablet} {
    align-items: end;
    justify-content: start;
    margin-inline-start: ${rem(4)};
    transition: opacity 0.2s 0.3s, visibility 0s 0.5s;

    &:not(.has-feedback) {
      opacity: 0;
      visibility: hidden;
    }
  }

  .thumb-up,
  .thumb-down {
    position: absolute;
    bottom: 0;

    &.always-visible {
      opacity: 1;
      visibility: visible;
      inset-inline-start: ${rem(2)};
    }

    // hide selected thumb-icon immediately
    &.hidden {
      display: none;
    }

    // fade-out not selected thumb-icon
    &.fade-out {
      -webkit-animation: fadeOut .25s ease-out;
      animation: fadeOut .25s ease-out;
      opacity: 0;
      margin: 0;

      @-webkit-keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }

      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }

    // thumb-up icon positioning
    &:not(.thumb-down) {
      inset-inline-start: ${rem(2)};

      &.move-feedback {
        -webkit-animation: moveThumbUp .25s ease-out;
        animation: moveThumbUp .25s ease-out;

        @-webkit-keyframes moveThumbUp {
            0% {inset-inline-start: ${rem(32)};}
          100% {inset-inline-start: ${rem(2)};}
        }

        @keyframes moveThumbUp {
            0% {inset-inline-start: ${rem(32)};}
          100% {inset-inline-start: ${rem(2)};}
        }
      }
    }

    // thumb-down icon positioning
    &:not(.thumb-up) {
      inset-inline-start: ${rem(32)};

      &.always-visible {
        inset-inline-start: ${rem(2)};
      }

      &.move-feedback {
        -webkit-animation: moveThumbDown .25s ease-out;
        animation: moveThumbDown .25s ease-out;

        @-webkit-keyframes moveThumbDown {
            0% {inset-inline-start: ${rem(32)};}
          100% {inset-inline-start: ${rem(2)};}
        }

        @keyframes moveThumbDown {
            0% {inset-inline-start: ${rem(32)};}
          100% {inset-inline-start: ${rem(2)};}
        }
      }
    }
  }
`

const FeedbackButton = styled.button`
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
`

const StyledBodyText = styled(BodyText)`
  display: inline;
  word-break: break-word;
  white-space: pre-wrap;
  text-align: initial;
`

interface FeedbackParams {
  messageId: string
  sentiment: 'POSITIVE' | 'NEGATIVE'
}

interface MessageProps {
  message?: App.ChatMessage
  processedMessage: string
}

function Message({
  message,
  processedMessage,
}: MessageProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const customerId = useAppSelector(state => getCurrentUserId(state))
  const currentChat = useAppSelector(state => selectCurrentChat(state))

  const [feedback, setFeedback] = useState(message?.feedback ?? undefined)
  const [playFeedbackAnimation, setPlayFeedbackAnimation] = useState(false)

  const showModal = useContext(ModalContext)
  const onFeedbackClick = useCallback(async(feedback: FeedbackParams) => {
    setPlayFeedbackAnimation(true)
    setFeedback(feedback.sentiment)
    const userFeedback = await showModal<undefined | any>(<FeedbackModal sentiment={feedback.sentiment} />)

    if (!(userFeedback?.issues?.length || userFeedback?.message)) {
      setFeedback(undefined)
      return
    }

    // send the feedback through the socket to add to the messages table json
    sendMessageFeedback({
      chatId: currentChat?.id,
      feedback: feedback.sentiment,
      messageId: feedback.messageId,
    })

    // send the API call to create the record in the feedback table
    dispatch(postMessageFeedback({
      chatId: currentChat?.id,
      customerId,
      ...userFeedback,
      ...feedback,
    }))
  }, [currentChat?.id, customerId, dispatch, showModal])

  if (!message) {
    return null
  }

  return (
    <Container className={clsx('message-container', { 'is-user-sender': isSenderUser(message.sender), 'is-le-sender': isSenderLE(message.sender), 'is-system-sender': isSenderSystem(message.sender) })}>
      {isSenderLE(message.sender) && <Avatar>
        {message.sender === 'chatbot' && <LuxuryEscapesSmallLogo height={24} width={24} />}
        {message.sender === 'agent' && <AgentAvatar width={24} height={24} />}
      </Avatar>}
      {message?.meta?.action && <MessageMetaContent className={clsx('message-meta', { 'is-user-sender': isSenderUser(message.sender), 'is-le-sender': isSenderLE(message.sender) })}>
        <MessageMetaComponents meta={message?.meta} />
      </MessageMetaContent>}
      {message.text && !isSenderSystem(message.sender) && <MessageContent className={clsx('message-content', { 'is-user-sender': isSenderUser(message.sender), 'is-le-sender': !isSenderUser(message.sender) })}>
        {!message.isTyping && isSenderLE(message.sender) && <MarkdownRender content={processedMessage} type="compact" fontSize="medium" />}
        {!message.isTyping && isSenderUser(message.sender) && <StyledBodyText variant="medium"><bdi>{message.text}</bdi></StyledBodyText>}
      </MessageContent>}
      {message.text && isSenderSystem(message.sender) && <SystemMessageContent className={clsx({ 'is-system-sender': isSenderSystem(message.sender) })}>
        <StyledBodyText variant="small" colour="light-foreground-alt">{message.text}</StyledBodyText>
      </SystemMessageContent>}
      {message.isTyping && <MessageContent className="message-content"><Loading size={6} /></MessageContent>}
      {isSenderLE(message.sender) && <MessageFeedback className={clsx('feedback', { 'has-feedback': feedback })}>
        {feedback && feedback === 'POSITIVE' && <SolidThumbsUpIcon className={clsx({ 'move-feedback': playFeedbackAnimation }, 'thumb-up', 'always-visible')} colour="light-foreground-alt" size="S" />}
        {!feedback && <FeedbackButton
          type="button"
          aria-label={intl.formatMessage({
            id: 'ZPwfuu61Uuq2ZY0-HFaH',
            defaultMessage: 'Send positive feedback for this assistant message',
            description: 'Accessibility label for the positive feedback button on an assistant chat message',
          })}
          onClick={() => onFeedbackClick({ messageId: message.id, sentiment: 'POSITIVE' })}
          className="thumb-up"
        >
          <LineThumbsUpIcon colour="light-foreground-alt" size="S" />
        </FeedbackButton>}
        {!feedback && <FeedbackButton
          type="button"
          aria-label={intl.formatMessage({
            id: 'X0IE5znevupMDSxsvfaD',
            defaultMessage: 'Send negative feedback for this assistant message',
            description: 'Accessibility label for the negative feedback button on an assistant chat message',
          })}
          onClick={() => onFeedbackClick({ messageId: message.id, sentiment: 'NEGATIVE' })}
          className="thumb-down"
        >
          <LineThumbsDownIcon colour="light-foreground-alt" size="S" />
        </FeedbackButton>}
        {feedback && feedback === 'NEGATIVE' && <SolidThumbsDownIcon className={clsx({ 'move-feedback': playFeedbackAnimation }, 'thumb-down', 'always-visible')} colour="light-foreground-alt" size="S" />}
      </MessageFeedback>}
    </Container>
  )
}

export default phoneLinksDecorator(Message)
